import { fetchAccessToken } from '../fetchAccessToken.ts';
import addParam from '../addParam.ts';
import { STORAGE_KEY } from '../constants.ts';

function checkForUpdates(url: string, fetchOptions: any, label?: boolean) {
    const limit = localStorage.getItem('contents_limit') || 1500;
    const checkURL = `${url}/module/delivery/bpca/api/v2/contents?fields-preset=full&limit=${limit}`;
    const buildInfoStorageKey = 'build-info' + (label ? '-label' : '');

    const CONTENT_UPDATE_KEY = 'content-update-available';
    const FRONTEND_UPDATE_KEY = 'frontend-update-available';

    function reloadWithUpdateParam() {
        if (label) {
            console.log('[UPDATE][LABEL] Reload blocked on label screen');
            return;
        }

        addParam('update', 'true');
        window.location.reload();
    }

    // Ensure headers for update checks
    if (fetchOptions && fetchOptions.headers) {
        fetchOptions.headers['Service-Worker'] = 'false';
        fetchOptions.headers['Cache-Control'] = 'max-age=0';
    }

    async function setFetchOptions() {
        const expiresAt = localStorage.getItem('accesstoken_expires_at');
        const isExpired = !expiresAt || Date.now() > Number(expiresAt);

        const token =
            navigator.onLine && isExpired
                ? await fetchAccessToken()
                : localStorage.getItem('accesstoken');

        fetchOptions = {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + token,
                'Service-Worker': 'false',
                'Cache-Control': 'max-age=0',
            },
        };
    }

    async function checkContentUpdate(): Promise<boolean> {
        let etag: string | null = localStorage.getItem('etag');

        try {
            await setFetchOptions();

            const fetchHeaders: Headers = new Headers({
                ...fetchOptions.headers,
            });

            if (etag) {
                fetchHeaders.set('If-None-Match', etag);
            }

            const response = await fetch(checkURL, {
                method: 'GET',
                headers: fetchHeaders,
            });

            if (etag && response.status === 200) {
                const newEtag = response.headers.get('Etag');

                if (newEtag && newEtag !== etag) {
                    console.log('[UPDATE][CONTENT] ETag changed:', {
                        old: etag,
                        new: newEtag,
                    });
                    localStorage.setItem('etag', newEtag);
                    localStorage.setItem(CONTENT_UPDATE_KEY, 'true');
                    return true;
                }
            } else if (!etag && response.status === 200) {
                etag = response.headers.get('Etag');
                if (etag) {
                    localStorage.setItem('etag', etag);
                }
            }
        } catch (e) {
            console.warn('[UPDATE][CONTENT] Error during content check:', e);
        }

        return false;
    }

    async function checkFrontendUpdate(): Promise<boolean> {
        try {
            const buildInfoResponse = await fetch('./build-info.json', {
                headers: {
                    'Service-Worker': 'false',
                    'Cache-Control':
                        'max-age=0, no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
            });

            if (!buildInfoResponse.ok) return false;

            const ct = buildInfoResponse.headers.get('content-type');
            if (!ct?.includes('application/json')) return false;

            const newBuildInfo = await buildInfoResponse.json();
            const oldBuildInfo = localStorage.getItem(buildInfoStorageKey);

            if (oldBuildInfo) {
                const parsedOldBuildInfo = JSON.parse(oldBuildInfo);

                const timestampChanged =
                    new Date(newBuildInfo.timestamp) >
                    new Date(parsedOldBuildInfo.timestamp);

                const versionChanged =
                    newBuildInfo.version !== parsedOldBuildInfo.version;

                if (timestampChanged || versionChanged) {
                    console.log('[UPDATE][FRONTEND] build-info changed:', {
                        oldTimestamp: parsedOldBuildInfo.timestamp,
                        newTimestamp: newBuildInfo.timestamp,
                        oldVersion: parsedOldBuildInfo.version,
                        newVersion: newBuildInfo.version,
                    });

                    localStorage.setItem(
                        buildInfoStorageKey,
                        JSON.stringify(newBuildInfo)
                    );
                    localStorage.setItem(FRONTEND_UPDATE_KEY, 'true');
                    return true;
                }
            } else {
                localStorage.setItem(
                    buildInfoStorageKey,
                    JSON.stringify(newBuildInfo)
                );
            }
        } catch (e) {
            console.warn(
                '[UPDATE][FRONTEND] Error during build-info check:',
                e
            );
        }

        return false;
    }

    async function applyUpdateWhenIdle() {
        console.log('[UPDATE] Applying update (idle)');

        if (label) {
            console.log(
                '[UPDATE][LABEL] applyUpdateWhenIdle blocked on label screen'
            );
            return;
        }

        const isIdle = sessionStorage.getItem(STORAGE_KEY) === null;

        if (!isIdle) {
            console.log('[UPDATE] Not idle → skip update');
            return;
        }

        const contentUpdateAvailable =
            localStorage.getItem(CONTENT_UPDATE_KEY) === 'true';
        const frontendUpdateAvailable =
            localStorage.getItem(FRONTEND_UPDATE_KEY) === 'true';

        console.log('[UPDATE] pending flags:', {
            contentUpdateAvailable,
            frontendUpdateAvailable,
        });

        if (!contentUpdateAvailable && !frontendUpdateAvailable) {
            console.log('[UPDATE] No pending update flags');
            return;
        }

        // Nur Content geändert -> Main-Screen einfach reloaden
        if (contentUpdateAvailable && !frontendUpdateAvailable) {
            console.log(
                '[UPDATE][CONTENT] Content update only → reload on main screen'
            );
            localStorage.removeItem(CONTENT_UPDATE_KEY);
            reloadWithUpdateParam();
            return;
        }

        // Frontend geändert -> erst SW-Update versuchen
        const reg = await navigator.serviceWorker.getRegistration();

        if (!reg) {
            console.log(
                '[SW] No service worker registration → reload fallback'
            );
            localStorage.removeItem(FRONTEND_UPDATE_KEY);
            localStorage.removeItem(CONTENT_UPDATE_KEY);
            reloadWithUpdateParam();
            return;
        }

        let updateFound = false;
        let installedAndActivated = false;

        const onUpdateFound = () => {
            updateFound = true;
            console.log('[SW] update found');

            const newWorker = reg.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
                console.log('[SW] state:', newWorker.state);

                if (
                    newWorker.state === 'installed' &&
                    navigator.serviceWorker.controller
                ) {
                    console.log('[SW] new version installed → activating');
                    installedAndActivated = true;
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                    localStorage.removeItem(FRONTEND_UPDATE_KEY);
                    localStorage.removeItem(CONTENT_UPDATE_KEY);
                }
            });
        };

        reg.addEventListener('updatefound', onUpdateFound);

        try {
            await reg.update();

            console.log('[SW] after update()', {
                waiting: !!reg.waiting,
                installing: !!reg.installing,
                active: !!reg.active,
                contentUpdateAvailable:
                    localStorage.getItem(CONTENT_UPDATE_KEY),
                frontendUpdateAvailable:
                    localStorage.getItem(FRONTEND_UPDATE_KEY),
                pendingReload: localStorage.getItem('pending-reload'),
            });

            if (reg.waiting) {
                console.log('[SW] waiting worker found → activating');
                reg.waiting.postMessage({ type: 'SKIP_WAITING' });
                localStorage.removeItem(FRONTEND_UPDATE_KEY);
                localStorage.removeItem(CONTENT_UPDATE_KEY);
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (localStorage.getItem('pending-reload') === 'true') {
                console.log(
                    '[SW] pending reload found → reloading main screen'
                );
                localStorage.removeItem('pending-reload');
                localStorage.removeItem(FRONTEND_UPDATE_KEY);
                localStorage.removeItem(CONTENT_UPDATE_KEY);
                reloadWithUpdateParam();
                return;
            }

            if (!updateFound && !installedAndActivated) {
                console.log(
                    '[UPDATE][FRONTEND] No new SW found → reload fallback on main screen'
                );
                localStorage.removeItem(FRONTEND_UPDATE_KEY);
                localStorage.removeItem(CONTENT_UPDATE_KEY);
                reloadWithUpdateParam();
            }
        } catch (e) {
            console.warn('[SW] Error during applyUpdateWhenIdle:', e);
        } finally {
            reg.removeEventListener('updatefound', onUpdateFound);
        }
    }

    if (!label) {
        void checkContentUpdate();
        void checkFrontendUpdate();
    }

    setInterval(
        async () => {
            if (label) {
                const hasPendingUpdate =
                    localStorage.getItem(CONTENT_UPDATE_KEY) === 'true' ||
                    localStorage.getItem(FRONTEND_UPDATE_KEY) === 'true';

                if (hasPendingUpdate) {
                    console.log(
                        '[UPDATE][LABEL] Update detected, waiting for main screen to handle reload'
                    );
                }
                return;
            }

            if (
                sessionStorage.getItem(STORAGE_KEY) === null &&
                navigator.onLine
            ) {
                console.log('[UPDATE] checking for updates ...');

                const contentChanged = await checkContentUpdate();
                const frontendChanged = await checkFrontendUpdate();

                if (
                    contentChanged ||
                    frontendChanged ||
                    localStorage.getItem(CONTENT_UPDATE_KEY) === 'true' ||
                    localStorage.getItem(FRONTEND_UPDATE_KEY) === 'true'
                ) {
                    await applyUpdateWhenIdle();
                }
            }
        },
        2 * 60000 * (label ? 0.1 : 1)
    );
}
export default checkForUpdates;
